import { Add, Close, DeleteOutline, Edit, MailOutline, Save, Schema as SchemaIcon } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, Divider, IconButton, Paper, Stack, TextField, Tooltip, Typography, alpha } from '@mui/material';

import { useEffect, useState } from 'react';

import { useToastService } from '@@common';

import { EnumModel, IntegrationEventAndPayloadModel } from '../../api/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import SchemaSection from '../EntityDesigner/Components/SchemaSection';

type IntegrationEventArtifactViewProps = {
	integrationEvent: IntegrationEventAndPayloadModel;
	schemaRefreshVersion: number;
	availableEnums: EnumModel[];
	onUpdateTags: (tags: string[]) => Promise<void>;
	onDelete: () => Promise<void>;
};

/** Focused artifact view for a single integration event and its editable payload schema. */
export default function IntegrationEventArtifactView({ integrationEvent, schemaRefreshVersion, availableEnums, onUpdateTags, onDelete }: IntegrationEventArtifactViewProps) {
	const { event, payload } = integrationEvent;
	const toast = useToastService();
	const [editingTags, setEditingTags] = useState(false);
	const [draftTags, setDraftTags] = useState<string[]>(event.tags ?? []);
	const [newTag, setNewTag] = useState('');
	const [savingTags, setSavingTags] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		setDraftTags(event.tags ?? []);
		setNewTag('');
		setEditingTags(false);
	}, [event.id, event.tags]);

	const addTag = () => {
		const normalizedTag = newTag.trim();
		if (!normalizedTag || draftTags.some((tag) => tag.localeCompare(normalizedTag, undefined, { sensitivity: 'accent' }) === 0)) {
			return;
		}
		setDraftTags((current) => [...current, normalizedTag]);
		setNewTag('');
	};

	const saveTags = async () => {
		setSavingTags(true);
		try {
			await onUpdateTags(draftTags);
			setEditingTags(false);
			toast.success('Integration event tags updated');
		} catch (error) {
			toast.error(`Failed to update tags: ${error instanceof Error ? error.message : 'Unknown error'}`);
		} finally {
			setSavingTags(false);
		}
	};

	const confirmDelete = async () => {
		setDeleting(true);
		try {
			await onDelete();
			setDeleteDialogOpen(false);
			toast.success('Integration event deleted');
		} catch (error) {
			toast.error(`Failed to delete integration event: ${error instanceof Error ? error.message : 'Unknown error'}`);
		} finally {
			setDeleting(false);
		}
	};

	return (
		<Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
			<Paper
				variant="outlined"
				sx={{
					p: 2.5,
					borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.45 : 0.3),
					backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.035),
				}}
			>
				<Stack direction="row" spacing={2} alignItems="flex-start">
					<Box
						sx={{
							display: 'grid',
							placeItems: 'center',
							width: 42,
							height: 42,
							borderRadius: 1.5,
							color: 'primary.main',
							bgcolor: 'background.paper',
							boxShadow: 1,
							flexShrink: 0,
						}}
					>
						<MailOutline />
					</Box>
					<Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
						<Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
							Integration Event
						</Typography>
						<Typography variant="h5" sx={{ fontWeight: 650, overflowWrap: 'anywhere' }}>
							{event.name}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{event.description || 'No description provided.'}
						</Typography>
						{editingTags ? (
							<Stack spacing={1} sx={{ pt: 0.75 }}>
								<Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
									{draftTags.map((tag) => (
										<Chip key={tag} label={tag} size="small" color="primary" onDelete={() => setDraftTags((current) => current.filter((item) => item !== tag))} />
									))}
								</Stack>
								<Stack direction="row" spacing={1} alignItems="center">
									<TextField
										value={newTag}
										onChange={(inputEvent) => setNewTag(inputEvent.target.value)}
										onKeyDown={(keyEvent) => {
											if (keyEvent.key === 'Enter') {
												keyEvent.preventDefault();
												addTag();
											}
										}}
										label="Add tag"
										size="small"
										sx={{ maxWidth: 220 }}
									/>
									<Button size="small" startIcon={<Add />} onClick={addTag} disabled={!newTag.trim() || savingTags}>
										Add
									</Button>
								</Stack>
								<Stack direction="row" spacing={1}>
									<Button size="small" variant="contained" startIcon={savingTags ? <CircularProgress size={14} color="inherit" /> : <Save />} onClick={() => void saveTags()} disabled={savingTags}>
										Save tags
									</Button>
									<Button
										size="small"
										startIcon={<Close />}
										onClick={() => {
											setDraftTags(event.tags ?? []);
											setNewTag('');
											setEditingTags(false);
										}}
										disabled={savingTags}
									>
										Cancel
									</Button>
								</Stack>
							</Stack>
						) : event.tags?.length ? (
							<Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ pt: 0.5 }}>
								{event.tags.map((tag) => (
									<Chip key={tag} label={tag} size="small" variant="outlined" color="primary" />
								))}
							</Stack>
						) : (
							<Typography variant="caption" color="text.secondary">
								No tags
							</Typography>
						)}
					</Stack>
					<Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
						<Tooltip title="Edit tags">
							<span>
								<IconButton size="small" aria-label="Edit tags" onClick={() => setEditingTags(true)} disabled={editingTags || deleting}>
									<Edit fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Delete integration event">
							<span>
								<IconButton size="small" color="error" aria-label="Delete integration event" onClick={() => setDeleteDialogOpen(true)} disabled={deleting}>
									{deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutline fontSize="small" />}
								</IconButton>
							</span>
						</Tooltip>
					</Stack>
				</Stack>
			</Paper>

			<Stack direction="row" spacing={1} alignItems="center">
				<SchemaIcon fontSize="small" color="primary" />
				<Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
					Payload
				</Typography>
				<Chip label={payload ? 'Schema' : 'None'} size="small" variant="outlined" />
				<Divider sx={{ flex: 1 }} />
			</Stack>

			{payload ? (
				<Box sx={{ minHeight: 0 }}>
					<SchemaSection schema={payload} availableEnums={availableEnums} refreshVersion={schemaRefreshVersion} />
				</Box>
			) : (
				<Paper variant="outlined" sx={{ py: 7, px: 3, textAlign: 'center', borderStyle: 'dashed' }}>
					<Typography variant="subtitle1">No payload schema</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						This integration event does not define a payload yet.
					</Typography>
				</Paper>
			)}

			<ConfirmDialog
				open={deleteDialogOpen}
				title="Delete Integration Event"
				message={`Delete "${event.name}"? This cannot be undone.`}
				confirmText="Delete"
				confirmColor="error"
				confirmDisabled={deleting}
				cancelDisabled={deleting}
				onConfirm={() => void confirmDelete()}
				onCancel={() => setDeleteDialogOpen(false)}
			/>
		</Stack>
	);
}
